import React, { useEffect, useMemo } from 'react';
import PatientPage from 'pages/rpm/patients/patient';
import useUser from 'utils/useUser';
import ModuleProvider from 'infrastructure/providers/module-provider';
import { useNavigate, useParams } from 'react-router-dom';
import { isValidGuid } from 'infrastructure/functions';
import {
  patientPageAccessCcmStatuses,
  patientPageAccessRpmStatuses,
} from 'infrastructure/consts';
import { showResult } from 'infrastructure/hooks/utils';
import { useTranslation } from 'react-i18next';
import { Routes } from 'router/routes';
import { useRpmPatientDetails } from 'infrastructure/hooks/rpm';
import BaseLoader from 'components/atoms/base-loader';
import ChartSessionTimer from 'components/atoms/chart-session-timer';
import {
  usePatientChartSession,
  IDLE_WARNING_NOTIFICATION_KEY,
} from 'infrastructure/hooks/patient/use-patient-chart-session';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import PatientActivityContext from 'infrastructure/providers/patient-activity-context';
import { useMultiPatientCall } from 'infrastructure/providers/multi-patient-call-context';

import UnifiedPatientProfile from './index';

const PatientProfile: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isPatient, hasClinicalRole } = useUser();
  const { patientGuid } = useParams();

  const {
    query: { patient, isLoading },
  } = useRpmPatientDetails({ patientGuid });

  const {
    sessionId,
    serverElapsedTime,
    rpmTime,
    ccmTime,
    idleState,
    onActivity,
  } = usePatientChartSession({
    patientGuid: patientGuid as string,
    canRun: !isPatient,
    onTimeout: () => {
      if (hasClinicalRole) {
        navigate(Routes.overviewDashboard);
      }
    },
  });

  const { onPatientVisited } = useMultiPatientCall();

  useEffect(() => {
    if (isPatient) return;
    if (patientGuid) onPatientVisited(patientGuid);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && patientGuid)
        onPatientVisited(patientGuid);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [patientGuid, onPatientVisited, isPatient]);

  useEffect(() => {
    if (hasClinicalRole && idleState === 'warning') {
      notificationController.warning({
        key: IDLE_WARNING_NOTIFICATION_KEY,
        message: t('messages.patientChartIdleWarning'),
        duration: 60,
      });
    } else if (idleState === null) {
      notificationController.destroy(IDLE_WARNING_NOTIFICATION_KEY);
    }
  }, [hasClinicalRole, idleState]);

  const hasAccess = useMemo(() => {
    if (!patient) return false;
    const hasRpmAccess = patientPageAccessRpmStatuses.includes(
      patient.profile.status,
    );
    const hasCcmAccess = patientPageAccessCcmStatuses.includes(
      patient.profile.ccmStatus,
    );
    return hasRpmAccess || hasCcmAccess;
  }, [patient]);

  useEffect(() => {
    if (!patientGuid || !isValidGuid(patientGuid)) {
      showResult(t('errors.invalidGuid'));
      navigate(Routes.notFound, { replace: true });
    }
  }, [patientGuid, navigate]);

  useEffect(() => {
    if (patient && !hasAccess) {
      navigate(Routes.notFound, { replace: true });
    }
  }, [patient, hasAccess]);

  return (
    <BaseLoader loading={isLoading || !hasAccess}>
      <PatientActivityContext.Provider value={{ onActivity }}>
        <ModuleProvider>
          {isPatient ? (
            <PatientPage />
          ) : (
            <UnifiedPatientProfile sessionId={sessionId} />
          )}
        </ModuleProvider>
      </PatientActivityContext.Provider>
      {patientGuid && (
        <ChartSessionTimer
          sessionId={sessionId}
          serverElapsedTime={serverElapsedTime}
          rpmTime={rpmTime}
          ccmTime={ccmTime}
        />
      )}
    </BaseLoader>
  );
};

export default PatientProfile;
